import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, ChevronLeft, ChevronRight, Warehouse, MapPin, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const Ubicaciones = () => {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ descripcion: '', ubicacion: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'locations');

  // Sorting
  const [sortField, setSortField] = useState('descripcion');
  const [sortOrder, setSortOrder] = useState('asc');
  const sortedUbicaciones = [...ubicaciones].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/config/ubicaciones?page=${pagination.page}&limit=${pagination.limit}`);
      setUbicaciones(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) { toast.error('Error al cargar ubicaciones'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [pagination.page]);

  const handleCreate = () => { setEditingItem(null); setFormData({ descripcion: '', ubicacion: '' }); setModalOpen(true); };
  const handleEdit = (item) => { setEditingItem(item); setFormData({ descripcion: item.descripcion, ubicacion: item.ubicacion || '' }); setModalOpen(true); };
  const handleSave = async () => {
    if (!formData.descripcion.trim()) { toast.error('La descripción es requerida'); return; }
    try {
      if (editingItem) { await api.put(`/config/ubicaciones/${editingItem.id}`, formData); toast.success('Ubicación actualizada'); }
      else { await api.post('/config/ubicaciones', formData); toast.success('Ubicación creada'); }
      setModalOpen(false); fetchData();
    } catch (error) { toast.error('Error al guardar'); }
  };
  const handleDelete = async (id) => { try { await api.delete(`/config/ubicaciones/${id}`); toast.success('Ubicación eliminada'); setDeleteConfirm(null); fetchData(); } catch (error) { toast.error('Error al eliminar'); } };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h1 className="page-title">Ubicaciones</h1><p className="page-subtitle">Gestión de ubicaciones internas</p></div>
          {canCreate && <Button className="btn btn-primary" onClick={handleCreate}><Plus className="h-4 w-4" /> Nueva Ubicación</Button>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="stats-card">
            <div className="mb-2"><Warehouse className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Ubicaciones</div>
          </div>
          <div className="stats-card">
            <div className="mb-2"><MapPin className="h-8 w-8 text-primary" strokeWidth={1.5} /></div>
            <div className="stats-card-value">--</div>
            <div className="stats-card-label">Sites</div>
          </div>
        </div>

        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (<div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div></div>) : (
              <>
                <table className="table-subtle">
                  <thead><tr>
                    <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('descripcion')}><div className="flex items-center gap-1.5">Descripción <SortIcon field="descripcion" /></div></th>
                    <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('ubicacion')}><div className="flex items-center gap-1.5">Ubicación <SortIcon field="ubicacion" /></div></th>
                    <th className="text-right">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {sortedUbicaciones.length === 0 ? (<tr><td colSpan={3}><div className="empty-state"><Warehouse className="h-12 w-12 text-muted-foreground mx-auto mb-3" /><p className="empty-state-text">No hay ubicaciones</p></div></td></tr>) : sortedUbicaciones.map((item) => (
                      <tr key={item.id}><td className="font-medium">{item.descripcion}</td><td className="text-muted-foreground">{item.ubicacion || '-'}</td>
                        <td className="text-right"><div className="flex justify-end gap-1">{canUpdate && <button onClick={() => handleEdit(item)} className="action-btn action-btn-edit"><Edit className="h-3.5 w-3.5" /></button>}{canDelete && <button onClick={() => setDeleteConfirm(item.id)} className="action-btn-delete"><Trash2 className="h-3.5 w-3.5" /></button>}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30"><p className="text-sm text-muted-foreground">{((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}</p><div className="flex items-center gap-1"><Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8"><ChevronLeft className="h-4 w-4" /></Button><span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span><Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8"><ChevronRight className="h-4 w-4" /></Button></div></div>
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Warehouse className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Ubicación' : 'Nueva Ubicación'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body space-y-4">
              <div><Label className="text-sm font-medium">Descripción *</Label><Input value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} className="input-subtle mt-1.5" /></div>
              <div><Label className="text-sm font-medium">Ubicación</Label><Input value={formData.ubicacion} onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })} className="input-subtle mt-1.5" /></div>
            </div>
            <DialogFooter className="modal-footer"><Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button><Button className="btn btn-primary" onClick={handleSave}>Guardar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="modal-body text-muted-foreground">¿Está seguro?</p>
            <DialogFooter className="modal-footer"><Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button><Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Ubicaciones;
